import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import * as actions from '../../../redux/actions/growthEngine';
import { toast, ToastContainer } from "react-toastify";
import { MetaAdPreview, createCreativeMediaRenderer } from './MetaAdPreview';
import { getTimezoneLabel, formatCampaignDateTime } from '../../../helper/utility';


export const DuplicateCampaignDetails = () => {
    const [showLoading, setShowLoading] = useState(false)
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { duplicatedCamapignDetails } = useSelector(state => state.growthEngine);
    const creatives = duplicatedCamapignDetails?.creatives || [];
    const adViewAs = duplicatedCamapignDetails?.adViewAs || 'facebook';
    const creative = creatives[0];

    useEffect(() => {
        if (!duplicatedCamapignDetails?.adViewAs) {
            dispatch(actions.updateDuplicateCampaign({ adViewAs: 'facebook' }));
        }
    }, [duplicatedCamapignDetails?.adViewAs, dispatch]);

    const facebookProfile = useMemo(() => ({
        name: duplicatedCamapignDetails?.pageInfo?.name || 'Facebook Page',
        image: duplicatedCamapignDetails?.pageInfo?.picture?.data?.url,
    }), [duplicatedCamapignDetails?.pageInfo]);

    const instagramProfile = useMemo(() => ({
        name: duplicatedCamapignDetails?.instaInfo?.username || 'Instagram',
        image: duplicatedCamapignDetails?.instaInfo?.profile_picture_url,
    }), [duplicatedCamapignDetails?.instaInfo]);

    const renderMedia = useMemo(
        () => createCreativeMediaRenderer(creatives),
        [creatives]
    );
    const dateFormatter = (isoString, type) => {
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0'); // Ensure 2 digits
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Ensure 2 digits
        const year = String(date.getFullYear()).slice(-2); // Get last 2 digits of year

        if (type === 'half') {
            const day = date.getDate();
            const month = date.toLocaleString("en-US", { month: "long" });

            // Add ordinal suffix (st, nd, rd, th)
            const ordinalSuffix = (d) => {
                if (d > 3 && d < 21) return "th";
                switch (d % 10) {
                    case 1: return "st";
                    case 2: return "nd";
                    case 3: return "rd";
                    default: return "th";
                }
            };

            return `${day}${ordinalSuffix(day)} ${month}`;
        }

        return `${day}/${month}/${year}`;
    };
    const formatToAmPm = (timeString) => {
        // console.log(timeString,'timeString========timeString')
        if (!timeString) return "";
        if (!timeString) return "";

    if (typeof timeString !== "string") {
        // Convert Date object to hours:minutes
        const date = new Date(timeString);
        if (isNaN(date)) return "";
        timeString = `${date.getHours()}:${date.getMinutes()}`;
    }
        const [hours, minutes] = timeString.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 || 12; // Convert 0 -> 12 AM, 13 -> 1 PM
        const formattedMinutes = String(minutes).padStart(2, '0'); // Ensure 2-digit minutes

        return `${formattedHours}:${formattedMinutes} ${period}`;
    };

    const handleDuplicateCampaign = () => {
        setShowLoading(true)
        let data = {
            campaignId: duplicatedCamapignDetails.campaignId,
            adAccountId:duplicatedCamapignDetails.adAccountId,
            budget: duplicatedCamapignDetails.budget,
            budgetType: duplicatedCamapignDetails.budgetType,
            startDate: duplicatedCamapignDetails.startDate,
            endDate: duplicatedCamapignDetails.endDate
        }
        dispatch(actions.duplicateCampaign({data,toast,navigate,setShowLoading}))
    }

    const handleCancel = () => {
        dispatch(actions.cancelDuplicateCampaign())
        navigate(-1);
    }
    return (
        <div className="container bg-theme_clr1">
            <ToastContainer />
            <div className="custom-wrapper max-w-[875px] mx-auto">
                <div className="font-inter text-theme_clr1 mb-[61px] relative">
                    {/* Heading */}
                    <div className="heading text-white font-medium text-center mb-[28.5px]">
                        <h5 className="text-[22.5px] mb-1">Duplicate Campaign Summary</h5>
                        <div className="description text-white text-opacity-80 text-[15px]">
                            Review the duplicated campaign details before finalizing.
                        </div>
                    </div>

                    <div className="info-box bg-white rounded-xl p-4 lg:p-5">
                        <div className="flex flex-col lg:flex-row gap-4 xl:gap-5">
                            {/* Left Section - Preview */}
                            <div className="left-info lg:w-[50%]">
                                <div className="heading mb-4">
                                    <div className="title-name text-base font-medium mb-2">
                                        Preview your ad
                                    </div>
                                    <div className="description font-medium text-theme_clr1 text-[15px] leading-normal">
                                        Toggle between platforms to see how your duplicated ad will look.
                                    </div>
                                </div>
                                <MetaAdPreview
                                    adViewAs={adViewAs}
                                    onViewChange={(view) => dispatch(actions.updateDuplicateCampaign({ adViewAs: view }))}
                                    facebookProfile={facebookProfile}
                                    instagramProfile={instagramProfile}
                                    adCaption={creative?.adCaption}
                                    adHeadline={creative?.headline}
                                    adDescription={creative?.description}
                                    callToAction={creative?.callToAction}
                                    destinationUrl={creative?.destinationUrl}
                                    renderMedia={renderMedia}
                                />
                            </div>

                            {/* Right Section - Details */}
                            <div className="right-info lg:w-[50%]">
                                <div className="heading mb-4">
                                    <div className="title-name text-base font-medium mb-2">
                                        Campaign details
                                    </div>
                                    <div className="description font-medium text-theme_clr1 text-[15px] leading-normal">
                                        Confirm your duplicated campaign settings.
                                    </div>
                                </div>
                                <div className="bg-[#F7FAFC] border border-black rounded-xl mb-4 p-4">
                                    <div className="item text-base font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">Goal</span>
                                        <span className="text-theme_clr1 text-right">{duplicatedCamapignDetails?.goal || ''}</span>
                                    </div>
                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">Start date</span>
                                        <span className="text-[#99989F] text-right">
                                            {duplicatedCamapignDetails?.startDate
                                                ? formatCampaignDateTime(duplicatedCamapignDetails.startDate, duplicatedCamapignDetails.timezone)
                                                : '-'}
                                        </span>
                                    </div>
                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">End date</span>
                                        <span className="text-[#99989F] text-right">
                                            {duplicatedCamapignDetails?.endDate
                                                ? formatCampaignDateTime(duplicatedCamapignDetails.endDate, duplicatedCamapignDetails.timezone)
                                                : '-'}
                                        </span>
                                    </div>
                                    {duplicatedCamapignDetails?.timezone ? (
                                        <div className="item text-[14.5px] font-medium flex justify-between gap-3 mb-5">
                                            <span className="text-theme_clr1">Timezone</span>
                                            <span className="text-[#99989F] text-right normal-case">{getTimezoneLabel(duplicatedCamapignDetails.timezone)}</span>
                                        </div>
                                    ) : null}
                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">Budget</span>
                                        <span className="text-[#99989F] text-right">{duplicatedCamapignDetails?.currency} {duplicatedCamapignDetails?.budget} ({duplicatedCamapignDetails?.budgetType})</span>
                                    </div>
                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">Audience</span>
                                        <span className="text-[#99989F] text-right">{duplicatedCamapignDetails?.gender === 'All' ? 'All Gender' : duplicatedCamapignDetails?.gender}, {duplicatedCamapignDetails?.ageRange.min}-{duplicatedCamapignDetails?.ageRange.max}</span>
                                    </div>
                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">Language</span>
                                        <span className="text-[#99989F] text-right">{duplicatedCamapignDetails?.languages?.map(lng => lng.name).join(", ") || "All"}</span>
                                    </div>
                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">Locations</span>
                                        <span className="text-[#99989F] text-right">
                                            {[
                                                ...(duplicatedCamapignDetails?.locations?.countries || []).map(country => country.name),
                                                ...(duplicatedCamapignDetails?.locations?.regions || []).map(region => region.name),
                                                ...(duplicatedCamapignDetails?.locations?.cities || []).map(city => city.name)
                                            ].join(", ") || "Global"}
                                        </span>
                                    </div>
                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">Interests</span>
                                        <span className="text-[#99989F] text-right">{duplicatedCamapignDetails?.interests?.map(interest => interest.name).join(", ")}</span>
                                    </div>
                                    <div className="item text-[14.5px] font-medium capitalize flex justify-between gap-3 mb-5">
                                        <span className="text-theme_clr1">Platform</span>
                                        <span className="text-[#99989F] text-right">Facebook, Instagram</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-theme_clr2 flex flex-wrap items-center justify-between gap-2 rounded-2xl p-4 xl:p-[18px]">
                <div className="left-sec text-theme_clr1 font-inter font-medium leading-none"></div>
                <div className="right-sec flex flex-wrap gap-3 lg:gap-4">
                    <button className="bg-theme_clr1 text-white text-xs font-inter font-medium tracking-[0.037] border border-white rounded-lg py-[10px] px-4"
                        onClick={() => handleCancel()}
                    >
                        <i className="bi bi-chevron-left"></i> Back
                    </button>
                    <button className="bg-white text-theme_clr1 text-xs font-inter font-medium tracking-[0.037] disabled:opacity-75 py-[10px] rounded-lg py-[10px] px-4"
                        onClick={() => handleDuplicateCampaign()}
                        disabled={showLoading}
                    >
                        {showLoading && <i className="bi bi-arrow-repeat animate-spin"></i>}
                        {showLoading ? "Duplicating..." : "Duplicate campaign"}
                        {/* Duplicate campaign */}
                    </button>
                </div>
            </div>
        </div>
    );
};
